import { FC, useEffect, ReactNode } from 'react'
import { X, Search } from 'lucide-react'

export interface ActionButton {
  label: string
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'danger'
}

interface Props {
  open: boolean
  onClose: () => void
  title: string
  wide?: boolean
  children: ReactNode
  actions?: ActionButton[]
  searchQuery?: string
  onSearchChange?: (query: string) => void
  searchPlaceholder?: string
  error?: string
}

const variantStyles: Record<string, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700',
  secondary: 'bg-slate-100 text-slate-600 hover:bg-slate-200',
  danger: 'bg-red-50 text-red-600 hover:bg-red-100',
}

const DashboardModal: FC<Props> = ({ open, onClose, title, wide, children, actions, searchQuery, onSearchChange, searchPlaceholder, error }) => {
  useEffect(() => {
    if (open) onSearchChange?.('')
  }, [open, onSearchChange])
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handler)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className='fixed inset-0 z-[1050] flex items-center justify-center p-4'>
      <div className='fixed inset-0 bg-black/40' onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-xl border border-slate-200 max-h-[90vh] overflow-y-auto ${wide ? 'w-full max-w-5xl' : 'w-full max-w-2xl'}`}>
        <div className='flex items-center justify-between p-4 border-b border-slate-200'>
          <h2 className='text-base font-bold text-slate-900'>{title}</h2>
          <button onClick={onClose} aria-label='Close details' className='w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all'>
            <X className='w-4 h-4' />
          </button>
        </div>

        {onSearchChange && (
          <div className='px-4 py-3 border-b border-slate-100'>
            <div className='relative'>
              <Search className='w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
              <input
                type='text'
                value={searchQuery || ''}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder || 'Search...'}
                className='w-full pl-9 pr-3 py-2 text-[11px] border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-blue-300 focus:outline-none transition-colors'
              />
            </div>
          </div>
        )}

        <div className='p-4'>{error ? <p role='status' className='text-sm text-slate-500'>{error}</p> : children}</div>

        {!error && actions && actions.length > 0 && (
          <div className='flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50 rounded-b-2xl'>
            {actions.map((action, i) => (
              <button
                key={i}
                onClick={action.onClick}
                className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${variantStyles[action.variant || 'secondary']}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardModal
